/**
 * ReactRouterAdapter — 基于 react-router-dom 的路由适配器实现
 *
 * 支持 SSR（服务端）和 CSR（客户端）两种环境：
 * - SSR: 使用纯内存数据结构，不调用 createBrowserRouter
 * - CSR: 使用 createBrowserRouter + RouterProvider
 *
 * 关键设计：init() 推迟到首次 getRouterComponent() 调用时才执行
 * createBrowserRouter()，避免 SSR 环境中访问 window/document。
 */
import * as React from 'react';
import { createBrowserRouter, RouterProvider, useNavigate, useLocation, useParams } from 'react-router-dom';
/**
 * 将 IPage 转换为 react-router 的路由对象
 *
 * 如果检测到 SSR hydration 场景（__SSR_config.enabled === true），
 * 则跳过 loader 执行，直接使用服务端预取的数据作为 initialData prop。
 */
function pagesToRoutes(pages) {
    // 检测是否为 SSR hydration 场景
    const ssrLoaderData = globalThis.__SSR_config?.loaderData;
    console.log('[Hydration:pagesToRoutes] pages count:', pages.length, ', ssrLoaderData keys:', ssrLoaderData ? Object.keys(ssrLoaderData) : 'none');
    return pages.map(page => {
        // 注入 initialData prop（如果 SSR 预取了数据）
        const element = page.component
            ? React.createElement(page.component, { initialData: ssrLoaderData?.[page.path] })
            : null;
        return {
            path: page.path,
            element,
            children: page.children ? pagesToRoutes(page.children) : undefined,
            loader: page.loader
                ? async (args) => {
                    console.log('[Hydration:loader] Executing loader for:', page.path, ', ssrData exists:', !!(ssrLoaderData && ssrLoaderData[page.path] !== undefined));
                    // SSR hydration 场景：如果预取数据存在，直接返回，避免重复请求
                    if (ssrLoaderData && ssrLoaderData[page.path] !== undefined) {
                        console.log('[Hydration:loader] Using SSR data for:', page.path);
                        return ssrLoaderData[page.path];
                    }
                    console.log('[Hydration:loader] Fetching fresh data for:', page.path);
                    const result = await page.loader({
                        params: args.params || {},
                        location: getCurrentLocationFromWindow(),
                    });
                    console.log('[Hydration:loader] Fresh data received for:', page.path);
                    return result;
                }
                : undefined,
        };
    });
}
/**
 * 从 window.location 构建 RouteLocation
 */
function getCurrentLocationFromWindow() {
    const loc = window.location;
    const searchParams = new URLSearchParams(loc.search);
    const query = {};
    searchParams.forEach((value, key) => {
        query[key] = value;
    });
    return {
        pathname: loc.pathname,
        search: loc.search,
        hash: loc.hash,
        params: {},
        query,
        href: loc.href,
    };
}
/**
 * 内部桥接组件 — 将 react-router 的 hooks 暴露给 adapter
 */
function RouterBridge({ adapter }) {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    React.useEffect(() => {
        adapter._navigate = (to, opts) => {
            navigate(to, { replace: opts?.replace, state: opts?.state });
        };
        adapter._params = params;
    }, [navigate, params, adapter]);
    React.useEffect(() => {
        adapter._location = {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            params: params,
            query: parseSearchParams(location.search),
            href: window.location.href,
        };
        adapter._listeners.forEach(fn => fn(adapter._location));
    }, [location, params, adapter]);
    return null;
}
function parseSearchParams(search) {
    const params = new URLSearchParams(search);
    const result = {};
    params.forEach((value, key) => {
        result[key] = value;
    });
    return result;
}
/**
 * 内部适配器类 — 持有路由表和 RouterProvider 组件
 */
class ReactRouterAdapterInner {
    constructor() {
        this._navigate = null;
        this._location = null;
        this._params = {};
        this._listeners = [];
        this.router = null;
        this.pages = [];
    }
    /**
     * 初始化路由表（不创建 browser router）
     *
     * 仅保存 pages 引用，推迟 createBrowserRouter 调用到
     * getRouterComponent() 时——避免 SSR 环境中
     * createBrowserRouter 访问 window/document。
     */
    init(pages, _options) {
        this.pages = pages;
        // 不在此处创建 browser router！推迟到 getRouterComponent()
    }
    updatePages(pages) {
        this.pages = pages;
        // 如果已经创建过 router，需要更新
        if (this.router) {
            this.router = createBrowserRouter([
                {
                    path: '/',
                    children: pagesToRoutes(pages),
                },
            ]);
        }
    }
    getRouterComponent() {
        const adapter = this;
        // 仅在首次调用时创建 browser router（客户端环境）
        if (!this.router) {
            console.log('[Hydration:Router] Creating browser router, pages:', this.pages.length);
            this.router = createBrowserRouter([
                {
                    path: '/',
                    children: pagesToRoutes(this.pages),
                },
            ]);
            console.log('[Hydration:Router] Browser router created');
        }
        return function AppRouter() {
            return React.createElement(RouterProvider, {
                router: adapter.router,
            });
        };
    }
    navigate(to, options) {
        if (this._navigate) {
            this._navigate(to, options);
        }
    }
    goBack() {
        if (this._navigate) {
            this._navigate(-1, undefined);
        }
    }
    goForward() {
        console.warn('[ReactRouter] goForward not fully supported');
    }
    getCurrentLocation() {
        return this._location ?? getCurrentLocationFromWindow();
    }
    listen(listener) {
        this._listeners.push(listener);
        return () => {
            const index = this._listeners.indexOf(listener);
            if (index > -1) {
                this._listeners.splice(index, 1);
            }
        };
    }
    getParams() {
        return this._params;
    }
    getQueryParams() {
        return this._location?.query ?? parseSearchParams(window.location.search);
    }
}
/**
 * React Router 适配器（对外暴露的类）
 */
export class ReactRouterAdapter extends ReactRouterAdapterInner {
}
//# sourceMappingURL=react-router-adapter.js.map